<template>
  <Modal
    :modal-active="modelValue"
    :has-header-border="true"
    :has-footer-border="true"
    max-width="max-w-6xl"
    max-height="90vh"
    data-testid="station-evidence-modal"
    @close="close"
  >
    <template #header>
      <h2 class="m-0 text-left text-lg text-textPrimary" data-testid="station-evidence-title">
        Evidência do registro de plantão
      </h2>
    </template>

    <!-- O `Tabs` entra apenas como barra de navegação: sem slots ele não renderiza
         área de conteúdo, e é isso que queremos. A área dele é um `<Transition>`
         keyed pela aba ativa, que DESMONTA o painel a cada troca — voltar para uma
         aba refaria a chamada e o download do arquivo. O corpo fica abaixo, sob
         nosso controle. -->
    <Tabs v-model="activeLeg" :tabs="tabItems" data-testid="station-evidence-tabs" />

    <!-- `v-if` de aba visitada + `v-show` de aba ativa: a aba nunca aberta não
         monta (zero requisição, e o mapa do Google nunca nasce dentro de um
         container `display:none`, onde renderizaria cinza), e a já aberta
         permanece viva (zero recarga ao voltar). -->
    <template v-for="leg in context.legs" :key="leg.key">
      <div
        v-if="visitedLegs.includes(leg.key)"
        v-show="activeLeg === leg.key"
        class="flex flex-col gap-4 pt-4 text-left"
        :data-testid="`station-evidence-panel-${leg.key}`"
      >
        <StationSourceOriginSummary
          v-if="leg.origin"
          :leg="leg.key"
          :source="leg.origin"
          :at="leg.at"
          :timezone="context.timezone"
        />

        <CheckVerificationPanel
          v-if="showsVerification(leg)"
          :check-id="leg.origin?.refId ?? null"
          :events="[leg.key]"
          :testid-prefix="`station-evidence-${leg.key}`"
          :map-height-px="200"
        />
        <StationSheetEvidencePanel
          v-else-if="showsSheet(leg)"
          :container-id="leg.origin?.dsAttendanceContainerId ?? null"
          :leg="leg.key"
        />
        <StationActivityEvidencePanel
          v-else-if="showsActivityRecord(leg)"
          :container-id="leg.origin?.dsAttendanceContainerId ?? null"
          :attachments="leg.attachments"
          :leg="leg.key"
        />

        <div
          v-else
          class="flex min-h-[240px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center"
          :data-testid="`station-evidence-empty-${leg.key}`"
        >
          <svg class="h-10 w-10 text-neutral50" viewBox="0 0 24 24" aria-hidden="true">
            <path :d="mdiImageOffOutline" fill="currentColor" />
          </svg>
          <p class="m-0 font-bold text-textPrimary">Sem arquivo para exibir</p>
          <p class="m-0 max-w-sm text-sm text-neutral50">{{ emptyReason(leg) }}</p>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <OutlineButton
          text="Fechar"
          width="100px"
          height="32px"
          data-testid="station-evidence-close-button"
          @click="close"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { mdiImageOffOutline } from '@mdi/js'

import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import Tabs, { type TabItem } from '@/components/designSystem/Tabs/Tabs.vue'
import CheckVerificationPanel from './CheckVerificationPanel.vue'
import StationActivityEvidencePanel from './StationActivityEvidencePanel.vue'
import StationSheetEvidencePanel from './StationSheetEvidencePanel.vue'
import StationSourceOriginSummary from './StationSourceOriginSummary.vue'
import { EffectiveSourceKind } from '@/constants/ds-attendance.constants'
import type {
  StationEvidenceContext,
  StationEvidenceLeg,
  StationLegKey
} from '@/types/station-evidence.types'

const props = defineProps<{
  modelValue: boolean
  context: StationEvidenceContext
}>()

const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

const activeLeg = ref<StationLegKey>(props.context.initialLeg)
const visitedLegs = ref<StationLegKey[]>([props.context.initialLeg])

const tabItems = computed<TabItem[]>(() =>
  props.context.legs.map((leg) => ({
    label: leg.title,
    value: leg.key,
    testId: `station-evidence-tab-${leg.key}`
  }))
)

const CHECK_KINDS = [EffectiveSourceKind.APP, EffectiveSourceKind.EXTERNAL_APP]

// Sem `refId` não há check para pedir mapa e biometria; sem container não há
// folha nem registro a baixar. Nesses casos a aba cai no estado vazio, que
// explica o motivo, em vez de mostrar um painel que erraria na primeira chamada.
const showsVerification = (leg: StationEvidenceLeg) =>
  Boolean(leg.origin && CHECK_KINDS.includes(leg.origin.kind) && leg.origin.refId)

const showsSheet = (leg: StationEvidenceLeg) =>
  Boolean(
    leg.origin?.kind === EffectiveSourceKind.SHEET && leg.origin.dsAttendanceContainerId != null
  )

const showsActivityRecord = (leg: StationEvidenceLeg) =>
  leg.origin?.kind === EffectiveSourceKind.ACTIVITY_RECORD

const EMPTY_REASONS: Partial<Record<EffectiveSourceKind, string>> = {
  [EffectiveSourceKind.AUTO_PERMANENCE]:
    'Gerada automaticamente pela permanência entre plantões encadeados — não há arquivo a exibir.',
  [EffectiveSourceKind.MANUAL_LEGACY]:
    'Check lançado à mão no painel, antes da aposentadoria do fluxo manual — não há arquivo a exibir.',
  [EffectiveSourceKind.LEGACY]:
    'Horário herdado da base anterior ao ranking de fontes — não há arquivo a exibir.'
}

function emptyReason(leg: StationEvidenceLeg): string {
  const origin = leg.origin
  if (!origin) return 'Esta perna ainda não tem fonte registrada.'
  if (CHECK_KINDS.includes(origin.kind)) {
    return 'O registro não trouxe a referência do check; não é possível abrir mapa e biometria.'
  }
  if (origin.kind === EffectiveSourceKind.SHEET) {
    return 'A linha conciliada não trouxe o identificador da folha.'
  }
  return EMPTY_REASONS[origin.kind] ?? 'Esta fonte não tem arquivo a exibir.'
}

// Visitar é definitivo: a aba entra na lista e nunca sai enquanto o modal viver.
watch(activeLeg, (leg) => {
  if (!visitedLegs.value.includes(leg)) visitedLegs.value.push(leg)
})

// Trocar de plantão com o modal aberto mostraria a evidência do plantão anterior.
watch(
  () => props.context,
  (context) => {
    activeLeg.value = context.initialLeg
    visitedLegs.value = [context.initialLeg]
  }
)

function close() {
  emit('update:modelValue', false)
}
</script>
